import { useLocation } from '@tanstack/react-router'
import { DrawerState, useRightDrawer } from 'features/rightDrawer'
import { useRolodexEntries } from 'features/rolodex'
import { useEffect, useMemo, useState } from 'react'
import { isGeckoOrganization, isGeckoSite, RolodexEntryType } from 'types'

// This hook expects an orgOrSiteID (can be an org ID or a site ID)
// It takes care of figuring out the org if on a site, inferring whether we're on a site or an org, etc.
export const useNavbarData = (orgOrSiteID: string | undefined) => {
  const location = useLocation()
  const {
    sites,
    getEntry,
    getOrganization,
    organizations,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries({
    type: [RolodexEntryType.SITE, RolodexEntryType.ORGANIZATION],
    order_by: [{ key: 'name', descending: 'false' }],
  })

  // Check if on admin route once (memoized separately to avoid recalculating on every pathname change)
  const isAdminRoute = useMemo(
    () =>
      location.pathname.startsWith('/admin/') ||
      location.pathname.startsWith('/health-check'),
    [location.pathname]
  )

  const { site, sitesForOrg, organization } = useMemo(() => {
    if (isAdminRoute) {
      return { site: undefined, sitesForOrg: [], organization: undefined }
    }

    if (orgOrSiteID) {
      const entry = getEntry(orgOrSiteID)
      if (entry && isGeckoSite(entry)) {
        const org = getOrganization(entry.tags.organization)
        const sitesForOrg = sites.filter((s) => s.tags.organization === org?.id)
        return {
          site: entry,
          organization: org,
          sitesForOrg,
        }
      }
      if (entry && isGeckoOrganization(entry)) {
        const sitesForOrg = sites.filter(
          (s) => s.tags.organization === entry.id
        )
        return { site: undefined, organization: entry, sitesForOrg }
      }
    }

    // No orgOrSiteID provided - show blank selectors
    return { site: undefined, organization: undefined, sitesForOrg: [] }
  }, [isAdminRoute, getEntry, getOrganization, orgOrSiteID, sites])

  // TODO: ENT-1266 -- Remove this when we are able to optimistically update
  // Goal here is to force refresh the data in the ForecastTopBar when a new Organization or Site is added
  // Since the Landing page (and useLandingPageData) exists as a child of the drawer, we have to react to
  // changes in state of the drawer itself. To prevent re-fetching the data every re-render that uses useLandingPageData
  // we set a state that says "the edit organization / site drawer was open", and "when it closes, refresh the data"
  const [forceRefreshData, setForceRefreshData] = useState(false)
  const { drawerState } = useRightDrawer()

  useEffect(() => {
    // A state of interest has been opened, let's prep the refresh
    if (
      (drawerState === DrawerState.EditGeckoOrganization ||
        drawerState === DrawerState.EditGeckoSite) &&
      !forceRefreshData
    ) {
      setForceRefreshData(true)
    }
    // The drawer has now been closed (after being in a state of interest), refresh the data and reset the variable
    if (drawerState === DrawerState.Closed && forceRefreshData) {
      forceRefresh()
      setForceRefreshData(false)
    }
  }, [drawerState, forceRefreshData, forceRefresh])

  return {
    organization,
    organizations,
    site,
    sites,
    sitesForOrg,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  }
}
